import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLocalized } from '../../utils/dateUtils';
import i18n from '../../i18n';
import { Button } from '../common/Button';
import { useCreditStore } from '../../stores/creditStore';
import { WithCreditsModal } from '../credits/WithCreditsModal';

const CreditSection: React.FC = () => {
  const { t } = useTranslation(['dashboard', 'credits', 'common']);
  const { balance, refreshBalance, transactionHistory, fetchLatestCreditsLogs, loadingHistory } =
    useCreditStore();

  useEffect(() => {
    if (balance === null) {
      refreshBalance();
    }
  }, [balance, refreshBalance]);

  useEffect(() => {
    if (transactionHistory.length === 0) {
      fetchLatestCreditsLogs(2);
    }
  }, [transactionHistory.length, fetchLatestCreditsLogs]);

  const totalCredits = balance || 0;

  return (
    <section className="border-2 flex flex-col rounded-2xl border-gray-300 bg-white p-6 shadow-xl transition-all duration-300">
      <div className="mb-8 flex items-start justify-between">
        <h1 className="text-[1.2rem] font-semibold text-secondary">
          {t('MyWallet', { ns: 'common', defaultValue: 'Mon Porte-monnaie' })}
        </h1>
        <WithCreditsModal>
          {openCreditsModal => (
            <Button
              variant="transparent-with-border"
              onClick={openCreditsModal}
              className="hidden md:flex"
            >
              {t('dashboard:wallet.manage')}
            </Button>
          )}
        </WithCreditsModal>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="w-1/5">
          <h2 className="mb-4 text-lg font-semibold text-secondary">
            {t('dashboard:wallet.credits')}
          </h2>
          <div className="text-5xl font-bold text-secondary">{totalCredits}</div>
        </div>

        <div className="w-4/5">
          <h2 className="text-lg font-semibold text-secondary">
            {t('dashboard:wallet.latestExpenses')}
          </h2>

          <div className="space-y-4">
            {loadingHistory ? (
              <div className="text-sm text-secondary">{t('common:loading')}</div>
            ) : transactionHistory.length === 0 ? (
              <div className="text-sm text-secondary">{t('credits:history.noTransactions')}</div>
            ) : (
              transactionHistory.map(item => (
                <div
                  key={item.id}
                  className="border-b-2 border-secondary-light p-2 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-medium text-secondary">{item.title}</h2>
                    <div className="text-xs font-bold text-secondary">
                      {item.credits} {t('credits:wallet.credits')}
                    </div>
                  </div>
                  <div className="flex">
                    <span className="text-xs text-secondary">
                      {formatDateLocalized(item.created_at, i18n.language, {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreditSection;
